<template>
  <AppContainer :is-loading="isSubmitting">
    <div class="space-y-4">
      <EPageTitleMobile
        :title="t(useTranslationPath(callerPath, 'header'))"
        :description="t(useTranslationPath(callerPath, 'description'))"
      />

      <EForm>
        <EFormField
          id="main_engineer"
          :label="t(useTranslationPath(callerPath, 'mainEngineerLabel'))"
          fieldset
        >
          <ERadioGroup v-model="isMainEngineer">
            <ERadio
              id="main_engineer_yes"
              name="main_engineer"
              :label="t(useTranslationPath(callerPath, 'yes'))"
              :value="true"
            />
            <ERadio
              id="main_engineer_no"
              name="main_engineer"
              :label="t(useTranslationPath(callerPath, 'no'))"
              :value="false"
            />
          </ERadioGroup>
        </EFormField>

        <UserSelection
          v-if="isMainEngineer !== null && !isMainEngineer"
          v-model="mainEngineer"
          :label="useTranslationPath(callerPath, 'selectExistingMainEngineer')"
          :label-new="useTranslationPath(callerPath, 'newMainEngineer')"
          :hint="useTranslationPath(callerPath, 'hintMainEngineer')"
          :on-search="fetchEngineers"
        />
      </EForm>
    </div>

    <template #bottom>
      <EButton
        :label="t(useTranslationPath(callerPath, 'next'))"
        :disabled="!engineerStore.engineerForm.mainEngineer || isSubmitting"
        block
        @click="onSubmit"
      />
    </template>
  </AppContainer>
</template>

<script setup>
import AppContainer from '@/components/AppContainer.vue'
import UserSelection from '@/components/UserSelection.vue'
import { useGoBack } from '@/composables/useGoBack.js'
import { useTranslationPath } from '@/composables/useTranslationPath.js'
import { useAppStore } from '@/stores/AppStore.js'
import { useAuthStore } from '@/stores/authenticated/AuthStore.js'
import { useEngineerStore } from '@/stores/authenticated/EngineerStore.js'
import { useWorkOrderStore } from '@/stores/authenticated/WorkOrderStore.js'
import { EButton, EForm, EFormField, EPageTitleMobile, ERadio, ERadioGroup } from '@expansivefm/eds'
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'

const { t } = useI18n()
const router = useRouter()
const { setCustomGoBack } = useGoBack()

const appStore = useAppStore()
const authStore = useAuthStore()
const engineerStore = useEngineerStore()
const workOrderStore = useWorkOrderStore()

const callerPath = 'views.authenticated.workOrders.engineers.index'

const isMainEngineer = ref(null)
const isSubmitting = ref(false)
const engineers = ref([])
const mainEngineer = ref(null)

onMounted(async () => {
  setCustomGoBack(() =>
    router.push({
      name: `work-order.index`,
    }),
  )

  engineerStore.$patch({
    engineerForm: {
      mainEngineer: null,
      engineers: [],
    },
  })

  appStore.$patch({ showBackButton: true })
})

watch(
  () => isMainEngineer.value,
  (newValue) => {
    if (newValue) {
      engineerStore.$patch({
        engineerForm: {
          mainEngineer: authStore.user,
          engineers: engineerStore.engineerForm.engineers.filter(
            (engineer) => engineer.id !== authStore.user.id,
          ),
        },
      })
    } else {
      engineerStore.$patch({
        engineerForm: {
          mainEngineer: null,
          engineers: [
            ...engineerStore.engineerForm.engineers.filter(
              (engineer) => engineer.id !== authStore.user.id,
            ),
            authStore.user,
          ],
        },
      })
    }
  },
  { immediate: true, deep: true },
)

watch(
  () => mainEngineer.value,
  (value) => {
    if (Number.isInteger(value)) {
      engineerStore.$patch({
        engineerForm: {
          mainEngineer: engineers.value.find((e) => e.id === value),
          engineers: engineerStore.engineerForm.engineers,
        },
      })
    } else {
      engineerStore.$patch({
        engineerForm: {
          mainEngineer: value,
          engineers: engineerStore.engineerForm.engineers,
        },
      })
    }
  },
  { deep: true },
)

const fetchEngineers = async (query) => {
  return await engineerStore.fetchEngineers({ name: query }).then(({ data }) => {
    engineers.value = data
    return data.filter((engineer) => engineer.id !== authStore.user.id)
  })
}

const onSubmit = async () => {
  isSubmitting.value = true

  appStore.$patch({ showBackButton: false })

  if (Number.isInteger(engineerStore.engineerForm.mainEngineer?.id)) {
    await router.push({
      name: 'work-order.engineers.assistants',
      params: { workOrderId: workOrderStore.workOrder.id },
    })
  } else {
    await engineerStore
      .createEngineer({ name: engineerStore.engineerForm.mainEngineer })
      .then(async ({ data }) => {
        engineerStore.$patch({
          engineerForm: {
            mainEngineer: data,
            engineers: engineerStore.engineerForm.engineers,
          },
        })

        await router.push({
          name: 'work-order.engineers.assistants',
          params: { workOrderId: workOrderStore.workOrder.id },
        })
      })
      .catch((error) => {
        toast(t(useTranslationPath(callerPath, 'errorCreatingEngineer')), {
          autoClose: 5000,
          position: toast.POSITION.BOTTOM_CENTER,
          type: 'error',
        })

        isSubmitting.value = false
      })
  }
}
</script>
